import { useForm } from 'react-hook-form';
import { useState, useEffect } from 'react';
import * as generic from '../../../components/Generico';
import { ClientePJService } from '../../../service/ClientePJService';
import { InputComponent } from '../../../components/InputComponent';
import { Panel } from 'primereact/panel';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Divider } from 'primereact/divider';
import { EconomicaCpj } from '../../../models/cpj/EconomicaCpj';
import { BottomActionBar } from '../../../components/ui/BottomActionBar';

export const InfEconomica = ({ toast, back, next, id, permss, tipoFrmEmpresa, soloLectura }) => {

  const omodel = new EconomicaCpj();
  const defaultValues = generic.funDefault(omodel.model);
  const { control, formState: { errors }, handleSubmit, reset, setError, getValues, setValue, clearErrors } = useForm({ defaultValues });
  const clientepjSrv = new ClientePJService();
  const { inNumber } = InputComponent({ omodel, errors, control, permss, id, soloLectura });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    editFicha();
    // eslint-disable-next-line
  }, [id]);

  const editFicha = () => {
    setIsLoading(true);
    clientepjSrv.consultarClientepj(id, 'p3').then(res => {
      const _clientepj = res.data.clientepj;
      reset(generic.renewValues(_clientepj, omodel.model));
    }).finally(() => {
      setIsLoading(false)
    });
  }

  const onSubmit = (data) => {
    setIsSaving(true);
    data.paso = 'p3';
    data.id = id;
    data.tipoFrmEmpresa = tipoFrmEmpresa;
    clientepjSrv.editarClientepj(data).then(res => {
      generic.funcSuccess(res, toast);
      next(id);
    }).catch(error => {
      generic.funcError(error, toast, setError);
    }).finally(() => setIsSaving(false));
  }

  const actualizarPatrimonio = (e) => {
    if (tipoFrmEmpresa === 'FRM_TOYOTA') {
      actualizarPatrimonioToyota(e);
      return;
    }
    const actvs = ['ACT_CORRIENTE', 'ACT_NO_CORRIENTE'];
    const pasvs = ['PAS_CORRIENTE', 'PAS_NO_CORRIENTE'];
    let totalPasivos = 0;
    let totalActivos = 0;
    actvs.forEach(itm => {
      if (getValues(itm) && !isNaN(getValues(itm))) {
        totalActivos += parseFloat(getValues(itm));
      }
    });
    pasvs.forEach(itm => {
      if (getValues(itm) && !isNaN(getValues(itm))) {
        totalPasivos += parseFloat(getValues(itm));
      }
    });
    setValue('TOT_ACTIVO', totalActivos);
    setValue('TOT_PASIVOS', totalPasivos);
    setValue('PATRIMONIO', totalActivos - totalPasivos);
  }

  const actualizarPatrimonioToyota = (e) => {
    const totalPasivos = getValues('TOT_PASIVOS') ?? 0;
    const totalActivos = getValues('TOT_ACTIVO') ?? 0;
    const ingresos = getValues('INGR_MENSUAL') ?? 0;
    const egresos = getValues('EGRE_MENSUAL') ?? 0;
    if (ingresos > totalActivos) {
      setError('INGR_MENSUAL', { type: 'manual', message: 'Los ingresos mensuales no pueden ser mayores al total de activos' });
    } else {
      clearErrors('INGR_MENSUAL');
    }
    if (egresos > totalPasivos) {
      setError('EGRE_MENSUAL', { type: 'manual', message: 'Los egresos mensuales no pueden ser mayores al total de pasivos' });
    } else {
      clearErrors('EGRE_MENSUAL');
    }

    setValue('PATRIMONIO', totalActivos - totalPasivos);
  }

  const onError = (errors) => {
    toast.current.show({ severity: 'error', summary: 'Error', detail: 'Se detectaron errores, por favor revise el formulario para corregir.', life: 3000 });
  }

  if (isLoading) {
    return (
      <div className="flex flex-column align-items-center justify-content-center p-5">
        <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
        <span className="mt-3 text-color-secondary font-medium text-lg">Consultando información...</span>
      </div>
    );
  }

  return (
    <>
      <Panel header={generic.renderPanelHeader('A.4', 'INFORMACIÓN ECONÓMICA DEL CLIENTE', 'pi pi-chart-line')} className="panel-ficha shadow-1 mb-4">
        <form id="infEconomicaForm" onSubmit={handleSubmit(onSubmit, onError)} className="p-fluid">

          <Divider align="center" className="mt-1 mb-3">
            <span className="p-tag p-tag-rounded p-tag-secondary">Estado de Resultados Mensual</span>
          </Divider>
          <div className="formgrid grid">
            {inNumber('INGR_MENSUAL', 2, true, { icon: 'pi pi-arrow-up-right', iconPosition: 'left', func: actualizarPatrimonio }, 6)}
            {inNumber('EGRE_MENSUAL', 2, true, { icon: 'pi pi-arrow-down-right', iconPosition: 'left', func: actualizarPatrimonio }, 6)}
          </div>

          <div className="grid formgrid mt-2">
            {/* COLUMNA ACTIVOS */}
            <div className="col-12 md:col-6 mb-2 md:mb-0">
              <div className="p-3 surface-50 border-round h-full flex flex-column shadow-1">
                {tipoFrmEmpresa !== 'FRM_TOYOTA' && (
                  <div>
                    <div className="text-center text-primary font-bold mb-3 border-bottom-1 pb-2 text-lg">ACTIVOS</div>
                    <div className="formgrid grid">
                      {inNumber('ACT_CORRIENTE', 2, true, { icon: 'pi pi-money-bill', iconPosition: 'left', func: actualizarPatrimonio }, 12)}
                      {inNumber('ACT_NO_CORRIENTE', 2, true, { icon: 'pi pi-building', iconPosition: 'left', func: actualizarPatrimonio }, 12)}
                    </div>
                  </div>
                )}

                <div className="mt-auto">
                  {tipoFrmEmpresa !== 'FRM_TOYOTA' && <Divider type="dashed" className="my-2" />}
                  <div className="bg-blue-50 p-2 border-round border-1 border-blue-200">
                    <div className="formgrid grid m-0">
                      {inNumber('TOT_ACTIVO', 2, true, { icon: 'pi pi-chart-bar', iconPosition: 'left', disabled: tipoFrmEmpresa !== 'FRM_TOYOTA', func: actualizarPatrimonio }, 12)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* COLUMNA PASIVOS */}
            <div className="col-12 md:col-6">
              <div className="p-3 surface-50 border-round h-full flex flex-column shadow-1">
                {tipoFrmEmpresa !== 'FRM_TOYOTA' && (
                  <div>
                    <div className="text-center text-primary font-bold mb-3 border-bottom-1 pb-2 text-lg">PASIVOS</div>
                    <div className="formgrid grid">
                      {inNumber('PAS_CORRIENTE', 2, true, { icon: 'pi pi-calendar-minus', iconPosition: 'left', func: actualizarPatrimonio }, 12)}
                      {inNumber('PAS_NO_CORRIENTE', 2, true, { icon: 'pi pi-calendar-plus', iconPosition: 'left', func: actualizarPatrimonio }, 12)}
                    </div>
                  </div>
                )}

                <div className="mt-auto">
                  {tipoFrmEmpresa !== 'FRM_TOYOTA' && <Divider type="dashed" className="my-2" />}
                  <div className="bg-blue-50 p-2 border-round border-1 border-blue-200">
                    <div className="formgrid grid m-0">
                      {inNumber('TOT_PASIVOS', 2, true, { icon: 'pi pi-chart-bar', iconPosition: 'left', disabled: tipoFrmEmpresa !== 'FRM_TOYOTA', func: actualizarPatrimonio }, 12)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* SECCIÓN PATRIMONIO */}
          <div className="grid mt-3">
            <div className="col-12 md:col-4 md:col-offset-4">
              <div className="bg-blue-100 p-2 border-round border-1 border-blue-300 shadow-2">
                <div className="formgrid grid m-0">
                  {inNumber('PATRIMONIO', 2, true, { icon: 'pi pi-wallet', iconPosition: 'left', disabled: true, min: -10000000000000 }, 12)}
                </div>
              </div>
            </div>
          </div>

          {tipoFrmEmpresa !== 'FRM_TOYOTA' && (
            <>
              <Divider align="center" className="mt-4 mb-3">
                <span className="p-tag p-tag-rounded p-tag-secondary">Año Anterior</span>
              </Divider>
              <div className="grid formgrid">
                {inNumber('INGR_ANUAL', 2, true, { icon: 'pi pi-arrow-up-right', iconPosition: 'left' }, 6)}
                {inNumber('EGRE_ANUAL', 2, true, { icon: 'pi pi-arrow-down-right', iconPosition: 'left' }, 6)}
              </div>
            </>
          )}
        </form>
      </Panel>
      <BottomActionBar onPrev={back} onNext={next} formId="infEconomicaForm" loading={isSaving} isReadOnly={soloLectura} />
    </>
  );
}
